<template>
  <div class="app-date-range-picker">
    <v-layout
      wrap
      row
    >
      <v-flex
        :pr-3="space"
        lg6
        class="app-date-range-picker-datepicker"
      >
        <AppDatePicker
          v-model="initial"
          :label="itemInitial.label"
          :placeholder="itemInitial.placeholder"
          :class="itemInitial.class"
          :min="initialMin"
          :max="initialMax"
          :v="itemInitial.v"
          :style="datePickerStyle"
          :disabled="itemInitial.readonly"
          :readonly="itemInitial.readonly"
        />
        <v-tooltip
          v-if="initialTooltipText"
          class="app-date-range-picker-tooltip-initial"
          top
        >
          <v-icon
            slot="activator"
            size="16px"
          >
            help_outline
          </v-icon>
          <span>{{ initialTooltipText }}</span>
        </v-tooltip>
      </v-flex>
      <v-flex
        lg6
        class="app-date-range-picker-datepicker"
      >
        <AppDatePicker
          v-model="end"
          :label="itemEnd.label"
          :placeholder="itemEnd.placeholder"
          :class="itemInitial.class"
          :min="endMin"
          :max="endMax"
          :v="itemEnd.v"
          :style="datePickerStyle"
          :disabled="itemEnd.readonly"
          :readonly="itemEnd.readonly"
        />
        <v-tooltip
          v-if="endTooltipText"
          class="app-date-range-picker-tooltip-end"
          top
        >
          <v-icon
            slot="activator"
            size="16px"
          >
            help_outline
          </v-icon>
          <span>{{ endTooltipText }}</span>
        </v-tooltip>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import AppDatePicker from './AppDatePicker'
import moment from 'moment'

export default {
  name: 'AppDateRangePicker',
  components: {
    AppDatePicker
  },
  props: {
    value: { type: Array, default: undefined },
    labels: { type: Array, default: () => [null, null] },
    placeholders: { type: Array, default: () => [null, null] },
    initialReadOnly: { type: Boolean, default: false },
    endReadOnly: { type: Boolean, default: false },
    initialTooltipText: { type: String, default: '' },
    endTooltipText: { type: String, default: '' },
    space: { type: Boolean, default: false },
    min: { type: String, default: undefined },
    max: { type: String, default: undefined },
    datePickerStyle: { type: Object, default: () => {} },
    dateFormat: {
      type: String,
      enum: ['simple', 'ISO8601'],
      default: 'ISO8601'
    },
    dateLabel: {
      type: Object,
      default: () => ({
        initialDate: 'Início',
        finalDate: 'Término'
      })
    },
    datePlaceholder: {
      type: Object,
      default: () => ({
        initialDate: 'Data de Início',
        finalDate: 'Data de Término'
      })
    },
    v: { type: Array, default: () => [] },
    daysRange: {
      type: Number,
      default: 0,
    },
    defaultDateRangeValue: { type: Array, default: undefined },
  },
  computed: {
    initial: {
      get: function () {
        if (this.value?.length > 1) return this.value[0]
        if (this.defaultDateRangeValue?.length > 1) {
          return this.defaultDateRangeValue[0]
        }
        return undefined
      },
      set: function (value) {
        let endMoment
        let startMoment = moment(value)
        let endValue = this.end

        // letting reset if startDate > endDate or endDate bigger than maxDate
        if (this.end) {
          endMoment = moment(this.end)
          if (startMoment.isAfter(endMoment) || (this.daysRange && this.isBiggerDateThanMaxValue(value, endMoment))) {
            endValue = ''
          }
        }
        this.$emit('input', [this.formatDateToIsoString(value), endValue])
        this.$emit('change', [this.formatDateToIsoString(value), endValue])
      }
    },
    end: {
      get: function () {
        if (this.value?.length > 1) return this.value[1]
        if (this.defaultDateRangeValue?.length > 1) return this.defaultDateRangeValue[1]
        return undefined
      },
      set: function (value) {
        if (this.initial) {
          const initialMoment = moment(this.initial)
            if ((this.daysRange && this.isSmallerDateThanMinValue(value, initialMoment))) {
            this.initial = ''
          }
        }
        this.$emit('input', [this.initial, this.formatDateToIsoString(value)])
        this.$emit('change', [this.initial, this.formatDateToIsoString(value)])
      }
    },
    itemInitial() {
      return {
        label: this.labels[0] || `${this.dateLabel.initialDate}`,
        icon: this.icon,
        v: this.v.length > 1 ? this.v[0] : {},
        readonly: this.initialReadOnly,
        placeholder: this.placeholders[0] || this.datePlaceholder.initialDate
      }
    },
    itemEnd() {
      return {
        label: this.labels[1] || `${this.dateLabel.finalDate}`,
        icon: this.icon,
        min: this.initial,
        v: this.v.length > 1 ? this.v[1] : {},
        readonly: this.endReadOnly,
        placeholder: this.placeholders[1]||this.datePlaceholder.finalDate
      }
    },
    initialMin() {
      if (!this.daysRange) {
        return this.min
      }
      return this.hasInitialOrEndDate && moment(this.end).subtract(this.daysRange, 'days').toISOString(true)
    },
    initialMax() {
      if (!this.daysRange) {
        return this.max
      }
      return this.hasInitialOrEndDate && moment(this.end).subtract(1, 'days').toISOString(true)
    },
    endMin() {
      if (!this.daysRange) {
        return this.itemEnd.min
      }
      return this.hasInitialOrEndDate && moment(this.initial).add(1, 'days').toISOString(true)
    },
    endMax() {
      if (!this.daysRange){
        return this.max
      }
      return this.hasInitialOrEndDate && moment(this.initial).add(this.daysRange, 'days').toISOString(true)
    },
    hasInitialOrEndDate () {
      return this.initial || this.end
    }
  },
  methods: {
    formatDateToIsoString(date) {
      try {
        if (this.dateFormat === 'ISO8601') {
          // adding 12hr for the timezone
          // + 11:59 still same day
          // - 12 still same day
          return moment(date).hour(12).format()
        }
        return date
      } catch (err) {
        console.log(err)
      }
    },
    isBiggerDateThanMaxValue(value, comparisonDate) {
      const maxDate = moment(value).add(this.daysRange, 'days')
      return comparisonDate.isAfter(maxDate)
    },
    isSmallerDateThanMinValue(value, comparisonDate) {
      const minDate = moment(value).subtract(this.daysRange, 'days')
      return comparisonDate.isBefore(minDate)
    }
  }
}
</script>

<style lang="stylus">
.app-date-range-picker
  .app-date-range-picker-datepicker
    display: flex

    .app-date-range-picker-tooltip-initial
      position: absolute
      margin-left: 2.5rem

    .app-date-range-picker-tooltip-end
      position: absolute
      margin-left: 3.5rem
</style>
